import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { clearSessionToken, setSessionToken } from "../lib/sessionToken";
import { resolvePostAuthPath } from "../lib/navigationState";

export default function VerifyPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState("");
  const [pendingToken, setPendingToken] = useState("");
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaBusy, setTwoFaBusy] = useState(false);

  const finishSession = async (data) => {
    if (!data?.token || !data?.user) {
      setStatus("error");
      setError("Sign-in response was incomplete");
      return;
    }
    setSessionToken(data.token);
    try {
      await api.get("/auth/me");
    } catch {
      clearSessionToken();
      setStatus("error");
      setError("Could not establish your session. Please try signing in again.");
      return;
    }
    login(data.token, data.user, data.client || null);
    setStatus("ok");
    setTimeout(() => navigate(resolvePostAuthPath(data.user, data.client || null)), 600);
  };

  const handleNeeds2fa = (data) => {
    setPendingToken(data.pending_token || "");
    setStatus("2fa");
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    if (!pendingToken || !twoFaCode.trim()) return;
    setTwoFaBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/challenge", {
        pending_token: pendingToken,
        code: twoFaCode.trim(),
      });
      await finishSession(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code");
    } finally {
      setTwoFaBusy(false);
    }
  };

  useEffect(() => {
    const token = params.get("token");
    const exchange = params.get("exchange");
    const jwt = params.get("jwt"); // legacy Google redirect; prefer exchange=

    if (exchange) {
      (async () => {
        try {
          const { data } = await api.post("/auth/exchange", { code: exchange });
          if (data.needs_2fa) {
            handleNeeds2fa(data);
            return;
          }
          await finishSession(data);
        } catch (e) {
          setStatus("error");
          setError(e.response?.data?.detail || "Google sign-in failed");
        }
      })();
      return;
    }

    if (jwt) {
      // Legacy Google redirect — keep Bearer in memory and mint an httpOnly cookie via /auth/session.
      (async () => {
        try {
          const { data } = await api.post(
            "/auth/session",
            {},
            { headers: { Authorization: `Bearer ${jwt}` } }
          );
          if (data?.token && data?.user) {
            await finishSession(data);
            return;
          }
          setSessionToken(jwt);
          const me = await api.get("/auth/me", { headers: { Authorization: `Bearer ${jwt}` } });
          await finishSession({ token: jwt, user: me.data.user, client: me.data.client || null });
        } catch (e) {
          setStatus("error");
          setError(e.response?.data?.detail || "Google sign-in failed");
        }
      })();
      return;
    }
    if (!token) {
      setStatus("error");
      setError("Missing token");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify", { token });
        if (data.needs_onboarding) {
          setStatus("ok");
          navigate(`/onboarding?token=${encodeURIComponent(data.onboarding_token)}`, { replace: true });
          return;
        }
        if (data.needs_2fa) {
          handleNeeds2fa(data);
          return;
        }
        await finishSession(data);
      } catch (e) {
        setStatus("error");
        setError(e.response?.data?.detail || "Invalid or expired link");
      }
    })();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-8" data-testid="verify-page">
      <div className="max-w-md text-center">
        <div className="label-eyebrow mb-4">TAĪSTU</div>
        {status === "verifying" && (
          <>
            <h1 className="font-heading text-3xl mb-3 tracking-tight">Verifying your link…</h1>
            <p className="t-meta">One moment.</p>
          </>
        )}
        {status === "2fa" && (
          <form onSubmit={submit2fa} className="text-left" data-testid="verify-2fa-form">
            <h1 className="font-heading text-3xl mb-3 tracking-tight text-center">Check your email.</h1>
            <p className="t-meta mb-5 text-center">Enter the 6-digit code to finish signing in.</p>
            <label className="label-eyebrow mb-1 block">Verification code</label>
            <input
              className="t-input mb-4"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={twoFaCode}
              onChange={(e) => {
                setTwoFaCode(e.target.value);
                setError("");
              }}
              data-testid="verify-2fa-code"
            />
            {error ? <p className="t-meta mb-3 text-[var(--t-accent)]">{error}</p> : null}
            <button type="submit" className="t-btn-primary w-full" disabled={twoFaBusy || !twoFaCode.trim()}>
              {twoFaBusy ? "Verifying…" : "Continue"}
            </button>
          </form>
        )}
        {status === "ok" && (
          <>
            <h1 className="font-heading text-3xl mb-3 tracking-tight">Signed in.</h1>
            <p className="t-meta">Redirecting to your portal.</p>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-heading text-3xl mb-3 tracking-tight">Link not valid.</h1>
            <p className="t-meta mb-6" data-testid="verify-error-msg">{error}</p>
            <button onClick={() => navigate("/")} className="t-btn-primary" data-testid="back-to-login-btn">Back to sign in</button>
          </>
        )}
      </div>
    </div>
  );
}
